import { formatPropertyAddress, formatStreetAddress } from '../utils/propertyAddress';
import { FleetLandingHeader, FleetLandingDock } from './FleetLandingNavigation';
import LandingNavIcon from './LandingNavIcon';
import { propertyFetch } from '../services/idxPropertyClient';
// src/components/LandingPage.js
import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ActionButtons from './ActionButtons';
import PropertyDetailModal from './PropertyDetailModal';
import usePropertyDetail from '../hooks/usePropertyDetail';
import BlogsPage from './BlogsPage';
import ReviewsPage from './ReviewsPage';
import PageSEO, { agentJsonLd } from './PageSEO';
import { SITE_CONFIG } from '../config';
import { useTheme } from '../contexts/ThemeContext';
import { requestPasswordReset, signIn, signUp } from '../services/authService';
import { submitChatLead } from '../services/chatLeadService';
import { hasAdminAccess } from '../utils/authUser';
import HoneypotField, { withHoneypotValue } from './HoneypotField';
import './LandingPage.css';
import './IntroVideo.css';

import './FleetLandingNavigation.css';

const LandingPage = ({ directListing = false, directSearch = false }) => {
  const { listingKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const landingPageConfig = SITE_CONFIG.LANDING_PAGE;
  const [activeSection, setActiveSection] = useState(null);
  const [showMapPage, setShowMapPage] = useState(false);
  const [showGlobeMap, setShowGlobeMap] = useState(directListing || directSearch || location.state?.openGlobeMap);
  const [, setShowGlobeMapResults] = useState(false);
  const [selectedCity, setSelectedCity] = useState(location.state?.initialCity || null);
  const [showBlogs, setShowBlogs] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showSignInDropdown, setShowSignInDropdown] = useState(false);
  const [signInUsername, setSignInUsername] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [registerData, setRegisterData] = useState({
    username: '',
    email: '',
    password: '',
    phoneNumber: '',
    name: ''
  });
  const [showIntroVideo, setShowIntroVideo] = useState(!directListing && !directSearch);
  const [showMLSDisclosure, setShowMLSDisclosure] = useState(false);
  const homeValueVideoRef = React.useRef(null);
  const imageWarmersRef = React.useRef([]);
  const warmedImageUrlsRef = React.useRef(new Set());
  const [showAgentSection, setShowAgentSection] = useState(false);
  const [showImagePageText, setShowImagePageText] = useState(false);
  const [showEPage, setShowEPage] = useState(false);
  const [showCityTiles, setShowCityTiles] = useState(false);
  const [featuredProperties, setFeaturedProperties] = useState([]);

  // Details and galleries load when a visitor opens a property.

  const [currentPropertyIndex, setCurrentPropertyIndex] = useState(0);
  const propertyTouchStartX = React.useRef(null);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectForm, setConnectForm] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    postalCode: '',
    smsConsent: false,
    serviceReq: 'buying'
  });
  const [submittingConnect, setSubmittingConnect] = useState(false);
  const [showConnectSuccess, setShowConnectSuccess] = useState(false);
  const [showHomeValueModal, setShowHomeValueModal] = useState(false);
  const [homeValueForm, setHomeValueForm] = useState({ name: '', email: '', phoneNumber: '', postalCode: '', smsConsent: false });
  const [submittingHomeValue, setSubmittingHomeValue] = useState(false);
  const [showHomeValueSuccess, setShowHomeValueSuccess] = useState(false);
  const [likedProperties, setLikedProperties] = useState(new Set());
  const { propertyDetail, closePropertyDetail, propertyDetailImages, imagesLoading, openPropertyFromSummary } = usePropertyDetail();

  const SERVICE_AREAS = [
    { label: 'Flint', city: 'Flint', state: 'MI', descriptor: 'City living · Local roots', imageUrl: landingPageConfig.CITY_IMAGES.FLINT },
    { label: 'Grand Blanc', city: 'Grand Blanc', state: 'MI', descriptor: 'Neighborhoods · Room to grow', imageUrl: landingPageConfig.CITY_IMAGES.GRAND_BLANC },
    { label: 'Davison', city: 'Davison', state: 'MI', descriptor: 'Small-town feel · Connected living', imageUrl: landingPageConfig.CITY_IMAGES.DAVISON },
    { label: 'Burton', city: 'Burton', state: 'MI', descriptor: 'Everyday convenience · Close to home', imageUrl: landingPageConfig.CITY_IMAGES.BURTON },
    { label: 'Swartz Creek', city: 'Swartz Creek', state: 'MI', descriptor: 'Community · Open spaces', imageUrl: landingPageConfig.CITY_IMAGES.SWARTZ_CREEK },
    { label: 'Fenton', city: 'Fenton', state: 'MI', descriptor: 'Lakes · Downtown living', imageUrl: landingPageConfig.CITY_IMAGES.FENTON },
  ];

  const openFreshPropertySearch = () => {
    sessionStorage.removeItem('globeMapData');
    window.history.replaceState(window.history.state,'','/search');
    setSelectedCity(null);
    setShowGlobeMap(true);
  };

  const handleAreaSearch = (e) => {const idx=e.target.value;if(idx==='')return;e.target.value='';const area=SERVICE_AREAS[idx];if(!area)return;
 sessionStorage.removeItem('globeMapData');
 const params=new URLSearchParams({city:area.city,state:area.state || SITE_CONFIG.STATE,location:area.city,view:'map'});
 setShowGlobeMap(true);
 navigate(`/search?${params}`);
};

  React.useEffect(() => {
    if (showIntroVideo && !showGlobeMap) {
      document.body.classList.add('intro-active');
    } else {
      document.body.classList.remove('intro-active');
    }
    return () => document.body.classList.remove('intro-active');
  }, [showIntroVideo, showGlobeMap]);

  React.useEffect(() => {
    if (!showIntroVideo || showGlobeMap) return undefined;

    const connection = navigator.connection || {};
    if (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '')) return undefined;

    const warmImage = (src, priority = 'low') => {
      if (!src || warmedImageUrlsRef.current.has(src)) return;
      warmedImageUrlsRef.current.add(src);
      const image = new Image();
      image.decoding = 'async';
      image.fetchPriority = priority;
      image.src = src;
      image.decode?.().catch(() => {});
      imageWarmersRef.current.push(image);
    };

    // Start the first scroll scene immediately after the hero paints so the large
    // editorial images are downloaded and decoded before the visitor reaches them.
    warmImage(landingPageConfig.EDITORIAL_IMAGES.LIVING_ROOM, 'high');
    warmImage(landingPageConfig.EDITORIAL_IMAGES.CONDO_INTERIOR, 'low');

    // The city tiles are several screens lower, so warm them without competing with
    // the hero and agent-story assets during the initial render.
    const warmCityTiles = () => {
      Object.values(landingPageConfig.CITY_IMAGES).forEach((src) => warmImage(src, 'low'));
    };
    const ric = window.requestIdleCallback;
    const id = ric ? ric(warmCityTiles, { timeout: 2500 }) : setTimeout(warmCityTiles, 1500);

    return () => {
      if (ric && window.cancelIdleCallback) window.cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, [showIntroVideo, showGlobeMap, landingPageConfig]);

  React.useEffect(() => {
    if (!featuredProperties.length) return;
    const connection = navigator.connection || {};
    if (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '')) return;

    featuredProperties.slice(0, 3).forEach((property) => {
      if (!property.first_photo_url || warmedImageUrlsRef.current.has(property.first_photo_url)) return;
      warmedImageUrlsRef.current.add(property.first_photo_url);
      const image = new Image();
      image.decoding = 'async';
      image.fetchPriority = 'low';
      image.src = property.first_photo_url;
      image.decode?.().catch(() => {});
      imageWarmersRef.current.push(image);
    });
  }, [featuredProperties]);


  React.useEffect(() => {
    // Hide intro video when on a direct listing route
    if (directListing && listingKey) {
      setShowIntroVideo(false);
      setShowGlobeMap(true);
    }
  }, [directListing, listingKey]);

  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const viewportHeight = window.innerHeight;

      if (showIntroVideo && !showGlobeMap) {
        if (scrollTop >= viewportHeight * 0.5 && !showAgentSection) {
          setShowAgentSection(true);
        } else if (scrollTop < viewportHeight * 0.5 && showAgentSection) {
          setShowAgentSection(false);
        }

        if (scrollTop >= viewportHeight * 1.5 && !showImagePageText) {
          setShowImagePageText(true);
        } else if (scrollTop < viewportHeight * 1.5 && showImagePageText) {
          setShowImagePageText(false);
        }

        if (scrollTop >= viewportHeight * 2.5 && !showEPage) {
          setShowEPage(true);
        } else if (scrollTop < viewportHeight * 2.5 && showEPage) {
          setShowEPage(false);
        }

        if (scrollTop >= viewportHeight * 3.5 && !showCityTiles) {
          setShowCityTiles(true);
        } else if (scrollTop < viewportHeight * 3.5 && showCityTiles) {
          setShowCityTiles(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showIntroVideo, showGlobeMap, showAgentSection, showImagePageText, showEPage, showCityTiles]);

  React.useEffect(() => {
    let raf = null;
    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
    const update = () => {
      raf = null;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const viewportHeight = window.innerHeight;
      // Distance (px) from the moment the featured section is centered in the viewport.
      const imgParallax = scrollTop - viewportHeight * 2;
      const agentParallax = clamp((scrollTop - viewportHeight) * 0.055, -38, 38);
      const homeValueParallax = clamp((scrollTop - viewportHeight * 3) * 0.035, -24, 24);
      document.documentElement.style.setProperty('--img-parallax', String(imgParallax));
      document.documentElement.style.setProperty('--agent-primary-parallax', `${agentParallax}px`);
      document.documentElement.style.setProperty('--agent-secondary-parallax', `${agentParallax * -0.65}px`);
      document.documentElement.style.setProperty('--home-value-parallax', `${homeValueParallax}px`);
    };
    const onScroll = () => {
      if (raf == null) raf = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf != null) cancelAnimationFrame(raf);
    };
  }, []);

  React.useEffect(() => {
    const video = homeValueVideoRef.current;
    if (!video || !showEPage) return undefined;

    const connection = navigator.connection || {};
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const shouldKeepPoster = reduceMotion || connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '');

    if (shouldKeepPoster) {
      video.pause();
      return undefined;
    }

    const playPromise = video.play();
    if (playPromise?.catch) playPromise.catch(() => {});

    return () => video.pause();
  }, [showEPage]);

  React.useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode]);

  React.useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setIsAdmin(hasAdminAccess(userData));
      } catch (error) {
        console.error('Error parsing stored user data:', error);
      }
    }

    // Fetch featured properties
    const fetchFeaturedProperties = async () => {
      try {
        // 1. Agent's MLS listings
        const mlsRes = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary/page?listAgentMlsId=${SITE_CONFIG.AGENT.MLS_ID}&daysonmarketSort=newold&firstPhoto=true&hasMedia=true&pageSize=10`, {});
        const mlsProps = mlsRes.ok ? ((await mlsRes.json()).properties || []) : [];

        // 2. Private listings
        let privateProps = [];
        try {
          const privateRes = await fetch(`https://tophamtechbe.com/api/treas/private/v1/listings?site_id=${SITE_CONFIG.SITE_ID}`, {
            headers: { 'x-api-key': 'treas-api-key-2025' }
          });
          if (privateRes.ok) {
            const privateData = await privateRes.json();
            privateProps = (privateData.listings || []).map(l => ({
              ...l,
              listing_key: `PRIVATE_${l.id}`,
              list_price: l.listing_type === 'sale' ? l.price_sale : l.price_lease_monthly,
              first_photo_url: l.photo_url || null,
              street_number: l.address_line_1,
              street_name: '',
              city: l.city,
              isPrivate: true
            }));
          }
        } catch (e) {}

        let combined = [...mlsProps, ...privateProps];

        // 3. If under 5, top up with recent Flint listings price high to low
        if (combined.length < 5) {
          const needed = 10 - combined.length;
          const existingKeys = new Set(combined.map(p => p.listing_key));
          const fallbackRes = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary/page?city=Flint&state=MI&minPrice=50000&priceSort=highlow&firstPhoto=true&hasMedia=true&pageSize=${needed + 5}`, {});
          if (fallbackRes.ok) {
            const fallbackData = await fallbackRes.json();
            const fallbackProps = (fallbackData.properties || []).filter(p => !existingKeys.has(p.listing_key));
            combined = [...combined, ...fallbackProps];
          }
        }

        const final = combined.slice(0, 10);

        // Fetch media_url for any property missing first_photo_url
        const withImages = await Promise.all(final.map(async (property) => {
          if (property.first_photo_url || property.isPrivate) return property;
          try {
            const mediaRes = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${property.listing_key}`, {});
            if (mediaRes.ok) {
              const mediaData = await mediaRes.json();
              const first = (mediaData.media || [])
                .filter(m => m.media_url || m.s3_link)
                .sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
              if (first) return { ...property, first_photo_url: first.media_url || first.s3_link };
            }
          } catch (e) {}
          return property;
        }));

        setFeaturedProperties(withImages);
      } catch (error) {
        console.error('Error fetching featured properties:', error);
      }
    };

    fetchFeaturedProperties();

    // Handle direct listing access
    if (directListing && listingKey) {
      // Store listing key for ActionButtons to use
      sessionStorage.setItem('directListingKey', listingKey);
    }

    // Track page view
    const trackPageView = async () => {
      try {
        await fetch('https://tophamtechbeb.com/aw-monitor/v1/page-views', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            site_id: SITE_CONFIG.SITE_ID,
            page_path: window.location.pathname,
            referrer: document.referrer || '',
            is_mobile: /Mobi|Android/i.test(navigator.userAgent),
            user_agent: navigator.userAgent,
            session_id: sessionStorage.getItem('session_id') || Math.random().toString(36).substr(2, 9)
          })
        });
      } catch (error) {
        console.error('Error tracking page view:', error);
      }
    };

    if (!sessionStorage.getItem('session_id')) {
      sessionStorage.setItem('session_id', Math.random().toString(36).substr(2, 9));
    }

    trackPageView();

    // Listen for globe map open event from AgentInfo
    const handleOpenGlobeMap = () => openFreshPropertySearch();

    window.addEventListener('openGlobeMap', handleOpenGlobeMap);

    return () => {
      window.removeEventListener('openGlobeMap', handleOpenGlobeMap);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- page/session tracking is mount-only

  const handleToggle = () => {
    console.log('Toggle clicked, current mode:', isDarkMode);
    toggleTheme();
  };

  const handleSignInToggle = () => {
    setShowSignInDropdown(!showSignInDropdown);
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setSigningIn(true);
    try {

      const response = await signIn({
        username: signInUsername,
        password: signInPassword,
        siteId: SITE_CONFIG.SITE_ID,

      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAdmin(hasAdminAccess(userData));
        localStorage.setItem('user', JSON.stringify(userData));

        setShowSignInDropdown(false);
        setShowForgotPassword(false);
        setSignInUsername('');
        setSignInPassword('');
        setResetEmail('');
      } else {
        alert('Login failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed. Please try again.');
    } finally {
      setSigningIn(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setSigningIn(true);
    try {

      const response = await signUp({
        ...registerData,
        siteId: SITE_CONFIG.SITE_ID,

      });

      if (response.ok) {
        // Auto sign in after registration
        const signInResponse = await signIn({
          username: registerData.username,
          password: registerData.password,
          siteId: SITE_CONFIG.SITE_ID,

        });

        if (signInResponse.ok) {
          const userData = await signInResponse.json();
          setUser(userData);
          setIsAdmin(hasAdminAccess(userData));
          localStorage.setItem('user', JSON.stringify(userData));

          setShowSignInDropdown(false);
          setRegisterData({ username: '', email: '', password: '', phoneNumber: '', name: '' });
        }
      } else {
        alert('Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      alert('Registration failed. Please try again.');
    } finally {
      setSigningIn(false);
    }
  };

  const handleLogOut = () => {

    setUser(null);
    setIsAdmin(false);
    setShowSignInDropdown(false);
    localStorage.removeItem('user');
  };

  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await requestPasswordReset({
        email: resetEmail,
        baseUrl: window.location.origin,
        siteId: SITE_CONFIG.SITE_ID
      });

      if (response.ok) {
        setResetSuccess(true);
      } else {
        alert('Failed to send reset link. Please try again.');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      alert('Failed to send reset link. Please try again.');
    }
  };

  const handleHomeValueSubmit = async (e) => {
    e.preventDefault();
    setSubmittingHomeValue(true);
    try {
      const params = withHoneypotValue(new URLSearchParams({
        postalCode: homeValueForm.postalCode,
        name: homeValueForm.name,
        phoneNumber: homeValueForm.phoneNumber,
        smsConsent: homeValueForm.smsConsent ? 'true' : 'false',
        serviceReq: 'estimate',
        email: homeValueForm.email,
        siteId: SITE_CONFIG.SITE_ID
      }), e.currentTarget);
      const response = await submitChatLead(params, { rememberContact: true });
      if (response.ok) {
        setShowHomeValueModal(false);
        setHomeValueForm({ name: '', email: '', phoneNumber: '', postalCode: '', smsConsent: false });
        setShowHomeValueSuccess(true);
        setTimeout(() => setShowHomeValueSuccess(false), 6000);
      } else {
        alert('Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Home value form error:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmittingHomeValue(false);
    }
  };

  const handleConnectSubmit = async (e) => {
    e.preventDefault();
    setSubmittingConnect(true);
    try {
      const params = withHoneypotValue(new URLSearchParams({
        postalCode: connectForm.postalCode,
        name: connectForm.name,
        phoneNumber: connectForm.phoneNumber,
        smsConsent: connectForm.smsConsent ? 'true' : 'false',
        serviceReq: connectForm.serviceReq,
        email: connectForm.email,
        siteId: SITE_CONFIG.SITE_ID
      }), e.currentTarget);

      const response = await submitChatLead(params, { rememberContact: true });

      if (response.ok) {
        setShowConnectModal(false);
        setShowConnectSuccess(true);
        setConnectForm({ name: '', email: '', phoneNumber: '', postalCode: '', smsConsent: false, serviceReq: 'buying' });
        setTimeout(() => setShowConnectSuccess(false), 5000);
      } else {
        alert('Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Connect form error:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmittingConnect(false);
    }
  };

  return (
    <div className="landing-page">
      <PageSEO
        title="David Hamady - Real Estate Agent in Flint, Michigan"
        description="Professional real estate services in Flint, Grand Blanc, Davison, Burton, and Genesee County. Expert guidance for buying and selling homes. Call (810) 814-0490."
        path="/"
        jsonLd={agentJsonLd()}
      />
      {/* Hidden content for SEO crawlers */}
      <div style={{position: 'absolute', left: '-9999px', top: '-9999px'}}>
        <h1>Real Estate Services - Buy, Sell, Lease Properties</h1>
        <p>Professional real estate agent David Hamady provides expert home buying, selling, and leasing services. Chat with our AI assistant for instant property search and real estate advice.</p>
        <h2>Services We Offer:</h2>
        <ul>
          <li>Home Buying Assistance - Find your dream home with expert guidance</li>
          <li>Property Selling Services - Get the best price for your home</li>
          <li>Rental and Leasing - Find the perfect rental property</li>
          <li>Real Estate Consultation - Professional advice for all your property needs</li>
          <li>Property Search - Advanced search tools and personalized recommendations</li>
        </ul>
        <h2>Why Choose David Hamady?</h2>
        <p>Local expertise, personalized service, and cutting-edge technology including AI-powered chat assistance for instant help with your real estate questions.</p>
      </div>

      <FleetLandingDock hidden={showBlogs || showReviews || showGlobeMap || showMapPage} isDarkMode={isDarkMode} onSearch={openFreshPropertySearch} onChat={() => navigate('/chat')} onJournal={() => navigate('/blogs')} onReviews={() => navigate('/reviews')} onCloseAccount={() => setShowSignInDropdown(false)} isAdmin={isAdmin} onAdmin={() => { window.location.href = '/admin'; }}><div className="signin-dropdown"><button type="button" className="fleet-dock-button signin-toggle" onClick={handleSignInToggle} aria-expanded={showSignInDropdown} aria-haspopup="dialog"><LandingNavIcon name="account" /><span>{user ? 'Account' : 'Sign in'}</span></button>{showSignInDropdown && (
            <div className="signin-dropdown-content" role="dialog" aria-label={user ? 'Account menu' : 'Sign in'}>
              {!user ? (
                showForgotPassword ? (
                  resetSuccess ? (
                    <div style={{textAlign: 'center', padding: '20px'}}>
                      <h3 style={{color: '#28a745', margin: '0 0 15px 0'}}>✓ Email Sent!</h3>
                      <p style={{margin: '0 0 20px 0', fontSize: '14px', color: '#666'}}>Please check your email for password reset instructions.</p>
                      <button
                        onClick={() => { setShowForgotPassword(false); setResetEmail(''); setResetSuccess(false); }}
                        className="toggle-form-btn"
                      >
                        Back to Sign In
                      </button>
                    </div>
                  ) : (
                    <div>
                      <form onSubmit={handlePasswordResetSubmit} className="signin-form">
                        <h3>Reset Password</h3>
                        <input
                          type="email"
                          placeholder="Enter your email"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          required
                        />
                        <button type="submit">
                          Send Reset Link
                        </button>
                      </form>
                      <button
                        onClick={() => { setShowForgotPassword(false); setResetEmail(''); }}
                        className="toggle-form-btn"
                      >
                        Back to Sign In
                      </button>
                    </div>
                  )
                ) : !showRegisterForm ? (
                  <div>
                    <form onSubmit={handleSignIn} className="signin-form">
                      <h3>Sign In</h3>
                      <input
                        type="text"
                        placeholder="Username or Email"
                        value={signInUsername}
                        onChange={(e) => setSignInUsername(e.target.value)}
                        required
                      />
                      <input
                        type="password"
                        placeholder="Password"
                        value={signInPassword}
                        onChange={(e) => setSignInPassword(e.target.value)}
                        required
                      />
                      <button type="submit" disabled={signingIn}>
                        {signingIn ? 'Signing In...' : 'Sign In'}
                      </button>
                    </form>
                    <button
                      onClick={() => setShowRegisterForm(true)}
                      className="toggle-form-btn"
                    >
                      Need an account? Register
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      style={{background: 'none', border: 'none', color: '#666', fontSize: '11px', textDecoration: 'underline', cursor: 'pointer', marginTop: '12px', padding: '4px 0'}}
                    >
                      Forgot Password?
                    </button>
                  </div>
                ) : (
                  <div>
                    <form onSubmit={handleRegister} className="signin-form">
                      <h3>Register</h3>
                      <input
                        type="text"
                        placeholder="Username"
                        value={registerData.username}
                        onChange={(e) => setRegisterData({...registerData, username: e.target.value})}
                        required
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                        required
                      />
                      <input
                        type="password"
                        placeholder="Password"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                        required
                      />
                      <input
                        type="tel"
                        placeholder="Phone Number"
                        value={registerData.phoneNumber}
                        onChange={(e) => setRegisterData({...registerData, phoneNumber: e.target.value})}
                        required
                      />
                      <input
                        type="text"
                        placeholder="Full Name"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({...registerData, name: e.target.value})}
                        required
                      />
                      <button type="submit" disabled={signingIn}>
                        {signingIn ? 'Registering...' : 'Register'}
                      </button>
                    </form>
                    <button
                      onClick={() => setShowRegisterForm(false)}
                      className="toggle-form-btn"
                    >
                      Have an account? Sign In
                    </button>
                  </div>
                )
              ) : (
                <div className="user-info">
                  <h3>User Info</h3>
                  <p><strong>Name:</strong> {user.username}</p>
                  <p><strong>Email:</strong> {user.email}</p>
                  <p><strong>Role:</strong> {user.roles?.join(', ')}</p>
                  <button onClick={handleLogOut} className="logout-btn">
                    Log Off
                  </button>
                </div>
              )}
            </div>
          )}</div></FleetLandingDock>
      <div className="fullscreen-chat-container">
        {showIntroVideo && (
          <section className="intro-video-overlay hero-shell" aria-label={`${SITE_CONFIG.AGENT.NAME} — Flint real estate`}>
            <div className="hero-ambient" aria-hidden="true">
              <span className="hero-glow hero-glow-warm"></span>
              <span className="hero-glow hero-glow-cool"></span>
              <span className="hero-flourish-arc"></span>
            </div>
            <span className="hero-side-label" aria-hidden="true">Flint & Genesee County · Michigan Real Estate</span>
            {!showGlobeMap && !showMapPage && <FleetLandingHeader brand={<div className="hero-brand">
              <span className="hero-brand-mark" aria-hidden="true">{SITE_CONFIG.AGENT.NAME.split(' ').map((w) => w[0]).join('')}</span>
              <span className="hero-brand-text">
                <strong>{SITE_CONFIG.AGENT.NAME}</strong>
                <span>HamadyRobinson, REALTORS®</span>
              </span>
            </div>} onAbout={() => navigate('/agent')}  onContact={() => setShowConnectModal(true)} onToggleTheme={handleToggle} isDarkMode={isDarkMode} />}
            <div className="hero-layout">
              <div className="hero-copy">
                <span className="hero-eyebrow">David Hamady · Flint &amp; Genesee County</span>
                <h1 className="hero-headline">
                  <span className="hero-headline-line"><span>Your Michigan move.</span></span>
                  <span className="hero-headline-line"><span className="hero-headline-accent">Made personal.</span></span>
                </h1>
                <p className="hero-subcopy">
                  Personal representation for buyers and sellers across Flint & Genesee County — deep local
                  insight, honest guidance, and personal service from first showing to final signature.
                </p>
                <div className="hero-cta-row">
                  <button type="button" className="hero-cta-primary" onClick={() => setShowConnectModal(true)}>
                    Work With {SITE_CONFIG.AGENT.NAME.split(' ')[0]}
                    <span className="hero-cta-arrow" aria-hidden="true">→</span>
                  </button>
                  <button type="button" className="hero-cta-ghost" onClick={() => navigate('/chat')}>
                    <span className="hero-cta-dot" aria-hidden="true"></span>
                    Chat With Us
                  </button>
                </div>
                <button type="button" className="hero-mobile-property-search" onClick={openFreshPropertySearch}>
                  <span>Search Properties</span>
                  <span aria-hidden="true">→</span>
                </button>
                <div className="hero-search">
                  <select className="hero-search-select" onChange={handleAreaSearch} defaultValue="" aria-label="Search by neighborhood">
                    <option value="" disabled>Search by neighborhood...</option>
                    {SERVICE_AREAS.map((area, i) => (
                      <option key={area.city} value={i}>{area.label}</option>
                    ))}
                  </select>
                  <svg className="hero-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                </div>
                <div className="hero-stats">
                  <div className="hero-stat"><strong>Buy</strong><span>Find your next home</span></div>
                  <div className="hero-stat"><strong>Sell</strong><span>Plan your next chapter</span></div>
                  <div className="hero-stat"><strong>Local</strong><span>Flint &amp; Genesee County</span></div>
                </div>
              </div>
              <div className="hero-media">
                <div className="hero-video-frame">
                  <video
                    className="hero-video"
                    src={landingPageConfig.VIDEOS.HERO}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    aria-hidden="true"
                  />
                  <span className="hero-video-scrim" aria-hidden="true"></span>
                  <span className="hero-video-outline" aria-hidden="true"></span>
                  <span className="hero-video-caption">Life in Flint — <em>come take a look</em></span>
                </div>
                <div className="hero-review-card hero-local-card">
                  <span className="hero-review-kicker">Flint &amp; Genesee County</span>
                  <h2>A local connection.<br />A clearer next step.</h2>
                  <p>Buying, selling, or finding your next rental? Start with a conversation with David.</p>
                  <a href="tel:+18108140490">(810) 814-0490 <span aria-hidden="true">↗</span></a>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="hero-scroll-cue"
              onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
            >
              <span>Discover more</span>
              <span className="hero-scroll-line" aria-hidden="true"></span>
            </button>
          </section>
        )}
        {showIntroVideo && (
          <>
            <div className="agent-intro-page" id="agent-story">
              {showAgentSection && (
                <div className="agent-editorial-layout">
                  <div className="agent-editorial-images" aria-hidden="true">
                    <div className="agent-editorial-image agent-editorial-image-primary">
                      <img src={landingPageConfig.EDITORIAL_IMAGES.LIVING_ROOM} alt="" decoding="async" fetchPriority="high" />
                    </div>
                    <div className="agent-editorial-image agent-editorial-image-secondary">
                      <img src={landingPageConfig.EDITORIAL_IMAGES.CONDO_INTERIOR} alt="" decoding="async" fetchPriority="low" />
                    </div>
                    <span className="agent-editorial-caption">Flint · Michigan</span>
                  </div>
                  <div className="agent-intro-card">
                    <span className="agent-intro-eyebrow">Local knowledge · Personal service</span>
                    <div className="agent-intro-heading">
                      <img src={landingPageConfig.AGENT_IMAGE} alt={SITE_CONFIG.AGENT.NAME} className="agent-intro-photo" />
                      <div className="agent-intro-text">
                        <span>Your trusted real estate advisor</span>
                        <h2>Meet {SITE_CONFIG.AGENT.NAME}</h2>
                      </div>
                    </div>
                    <p className="agent-intro-copy">Dedicated to providing exceptional service and expert guidance through every step of your home buying or selling journey.</p>
                    <button className="agent-intro-btn" onClick={() => navigate('/agent')}>Discover David's Approach</button>
                  </div>
                </div>
              )}
            </div>
            <div className={`image-page ${showImagePageText ? 'visible' : ''}`} id="featured-properties">
              {showImagePageText && (
                <>
                  <div className="featured-section-ornaments" aria-hidden="true">
                    <div className="featured-flourish featured-flourish-top">
                      <span></span>
                      <span></span>
                    </div>
                    <span className="featured-tulsa-watermark">Flint</span>
                    <div className="featured-flourish featured-flourish-bottom">
                      <span></span>
                      <span></span>
                    </div>
                    <div className="featured-ornament-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <span className="featured-ornament-baseline"></span>
                  </div>
                  <div className="image-page-text">
                    <span className="image-page-kicker">Curated Flint Collection</span>
                    <h1>
                      <span>Ready for your</span>
                      <span className="image-page-title-accent">Flint Move?</span>
                    </h1>
                    <h2>With David Hamady at HamadyRobinson, REALTORS®</h2>
                    <p>A unique collection of rare and exclusive properties. Customize your search for the perfect property by using our advanced search technology.</p>
                  </div>
                  <div className="featured-properties-widget">
                    <h3>Featured Properties</h3>
                    {featuredProperties.length > 0 && (
                      <>
                        <div
                          className="property-carousel"
                          onTouchStart={(e) => { propertyTouchStartX.current = e.touches[0].clientX; }}
                          onTouchEnd={(e) => {
                            if (propertyTouchStartX.current === null) return;
                            const delta = e.changedTouches[0].clientX - propertyTouchStartX.current;
                            if (Math.abs(delta) > 40) {
                              setCurrentPropertyIndex((prev) =>
                                delta < 0
                                  ? (prev + 1) % featuredProperties.length
                                  : (prev - 1 + featuredProperties.length) % featuredProperties.length
                              );
                            }
                            propertyTouchStartX.current = null;
                          }}
                        >
                          <div className="property-carousel-track">
                            {featuredProperties.map((property, index) => {
                              let offset = index - currentPropertyIndex;
                              const halfwayPoint = Math.ceil(featuredProperties.length / 2);
                              if (offset > halfwayPoint) offset -= featuredProperties.length;
                              if (offset < -halfwayPoint) offset += featuredProperties.length;
                              const isActive = offset === 0;
                              return (
                                <button
                                  key={property.listing_key || index}
                                  type="button"
                                  className={`featured-property-card ${isActive ? 'active' : ''}`}
                                  style={{
                                    transform: `translateX(${offset * 74}%) translateZ(${isActive ? 28 : -80}px) scale(${isActive ? 1.035 : 0.78}) rotateY(${offset * -3}deg)`,
                                    opacity: Math.abs(offset) > 2 ? 0 : isActive ? 1 : 0.34,
                                    pointerEvents: isActive ? 'auto' : 'none',
                                    zIndex: 10 - Math.abs(offset)
                                  }}
                                  tabIndex={isActive ? 0 : -1}
                                  aria-hidden={!isActive}
                                  aria-label={`View featured property at ${formatPropertyAddress(property)}`}
                                  onClick={() => {
                                    if (isActive) {
                                      window.history.pushState(null, '', `/listing/${property.listing_key}`);
                                      openPropertyFromSummary(property);
                                    } else {
                                      setCurrentPropertyIndex(index);
                                    }
                                  }}
                                >
                                  <img src={property.first_photo_url} alt={formatStreetAddress(property)} decoding="async" />
                                  <span className="property-card-frame" aria-hidden="true"></span>
                                  <span className="property-residence-tag">Featured Residence</span>
                                  <span className="property-info">
                                    <span className="property-price">${Number(property.current_price ?? property.list_price ?? 0).toLocaleString()}</span>
                                    <span className="property-address">{formatPropertyAddress(property)}</span>
                                    <span className="property-view-link">View Property <span aria-hidden="true">↗</span></span>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div className="property-gallery-footer">
                          <div className="property-gallery-status" aria-live="polite">
                            <span className="property-gallery-count"><strong>{String(currentPropertyIndex + 1).padStart(2, '0')}</strong> / {String(featuredProperties.length).padStart(2, '0')}</span>
                            <button
                              type="button"
                              className="property-gallery-progress"
                              aria-label="Show next featured property"
                              onClick={(event) => {
                                event.stopPropagation();
                                setCurrentPropertyIndex((previous) => (previous + 1) % featuredProperties.length);
                              }}
                            >
                              <span style={{width: `${((currentPropertyIndex + 1) / featuredProperties.length) * 100}%`}}></span>
                            </button>
                          </div>
                          <div className="property-nav-buttons">
                            <button aria-label="Previous property" onClick={(e) => { e.stopPropagation(); setCurrentPropertyIndex((prev) => (prev - 1 + featuredProperties.length) % featuredProperties.length); }}>←</button>
                            <div className="property-dots">
                              {featuredProperties.map((_, index) => (
                                <button
                                  key={index}
                                  aria-label={`Go to property ${index + 1}`}
                                  className={`property-dot ${index === currentPropertyIndex ? 'active' : ''}`}
                                  onClick={(e) => { e.stopPropagation(); setCurrentPropertyIndex(index); }}
                                />
                              ))}
                            </div>
                            <button aria-label="Next property" onClick={(e) => { e.stopPropagation(); setCurrentPropertyIndex((prev) => (prev + 1) % featuredProperties.length); }}>→</button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="e-image-page" id="home-value">
              {showEPage && (
                <>
                  <video
                    ref={homeValueVideoRef}
                    className="home-value-video"
                    muted
                    loop
                    playsInline
                    preload="none"
                    poster={landingPageConfig.EDITORIAL_IMAGES.CONDO_INTERIOR}
                    aria-hidden="true"
                  >
                    <source src={landingPageConfig.VIDEOS.HOME_VALUE} type="video/mp4" />
                  </video>
                  <div className="home-value-video-overlay" />
                  <div className="home-value-section">
                  <div className="home-value-content-col">
                    <span className="home-value-eyebrow">Free for Homeowners</span>
                    <h2 className="e-image-heading">What's Your Home Worth?</h2>
                    <p className="home-value-subtext">Get a personalized home value estimate — no obligation, no pressure. Just honest insight from a local expert who knows the Flint market.</p>
                    <button className="home-value-btn" onClick={() => setShowHomeValueModal(true)}>Get My Free Estimate</button>
                    <span className="home-value-assurance">Confidential · Local · No obligation</span>
                  </div>
                  </div>
                </>
              )}
            </div>
            <div className="city-tiles-page" id="service-areas">
            {showCityTiles && (
              <>
                <div className="city-tiles-heading">
                  <span className="city-tiles-eyebrow">The Flint & Genesee County Guide</span>
                  <h2 className="city-tiles-title">Explore Our <em>Service Areas</em></h2>
                  <p>Distinct communities, thoughtfully matched to the way you want to live.</p>
                </div>
                <div className="city-tiles-grid">
                  {SERVICE_AREAS.map((area, index) => (
                    <button
                      key={area.city}
                      type="button"
                      className={`city-tile ${index === 0 ? 'city-tile-featured' : ''}`}
                      style={{'--city-image': `url("${area.imageUrl}")`, '--tile-index': index}}
                      onClick={() => navigate(area.city === 'Fenton' ? '/fenton-mi-homes-for-sale' : `/neighborhood/${area.city.toLowerCase().replace(/ /g, '-')}`)}
                      aria-label={`Explore real estate in ${area.label}, Michigan`}
                    >
                      <img className="city-tile-image" src={area.imageUrl} alt="" decoding="async" />
                      <span className="city-tile-frame"></span>
                      <span className="city-tile-content">
                        <span className="city-tile-heading-row">
                          <span className="city-tile-marker"></span>
                          <span className="city-tile-label">{area.label}</span>
                        </span>
                        <span className="city-tile-descriptor">{area.descriptor}</span>
                        <span className="city-tile-link">Explore the area <span aria-hidden="true">↗</span></span>
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          </>
        )}

      </div>

      {showMapPage && (
        <div className="full-map-page">
          <button
            className="close-map-page"
            onClick={() => setShowMapPage(false)}
          >
            ← Back to Chat
          </button>
          <ActionButtons
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            isMapPage={true}
            onMapPageClose={() => setShowMapPage(false)}
            user={user}
            isAdmin={isAdmin}
            setUser={setUser}
            setIsAdmin={setIsAdmin}
          />
        </div>
      )}
      {showGlobeMap && (
        <div className="full-map-page">
          <ActionButtons
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            isGlobeMap={true}
            onGlobeMapClose={() => {
              sessionStorage.removeItem('globeMapData');
              window.history.replaceState(window.history.state,'','/');
              setSelectedCity(null);
              setShowGlobeMap(false);
              setShowGlobeMapResults(false);
              setShowIntroVideo(true);
            }}
            onGlobeMapResultsChange={setShowGlobeMapResults}
            directListingKey={directListing ? listingKey : null}
            initialCity={selectedCity}
            user={user}
            isAdmin={isAdmin}
            setUser={setUser}
            setIsAdmin={setIsAdmin}
          />
        </div>
      )}
      {showBlogs && (
        <BlogsPage onClose={() => setShowBlogs(false)} />
      )}
      {showReviews && (
        <ReviewsPage onClose={() => setShowReviews(false)} />
      )}

      <footer className={`landing-footer ${(!showIntroVideo || showGlobeMap || showAgentSection) ? 'hidden' : ''}`}>
        <a href="/privacy-policy" className="footer-link">Privacy Policy</a>
        <span className="footer-separator"> | </span>
        <a href="/terms-of-service" className="footer-link">Terms of Service</a>
        <span className="footer-separator"> | </span>
        <button
          onClick={() => setShowMLSDisclosure(true)}
          className="footer-link mls-disclosure-link"
        >
          MLS Disclosure
        </button>
        <span className="footer-separator-logo"> | </span>
        <a href="https://agentwinds.com" target="_blank" rel="noopener noreferrer">
          <img src={landingPageConfig.FOOTER_LOGO} alt="Websites by AW" className="footer-logo" />
        </a>
      </footer>

      {showMLSDisclosure && (
        <div className="modal-overlay" onClick={() => setShowMLSDisclosure(false)}>
          <div className="mls-disclosure-modal" onClick={(e) => e.stopPropagation()}>
            <h3>MLS Disclosure</h3>
            <p>
              The data relating to real estate for sale on this website comes in part from the Internet Data Exchange (IDX) program of the East Central Association of REALTORS®. Real estate listings held by brokerage firms other than HamadyRobinson are marked with the IDX logo or an abbreviated logo and detailed information about them includes the name of the listing broker.
            </p>
            <p>
              Data is deemed reliable but is not guaranteed accurate by the MLS or this website. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.
            </p>
            <p>
              Copyright © 2026 East Central Association of REALTORS®. All rights reserved.
            </p>
            <button onClick={() => setShowMLSDisclosure(false)} className="close-disclosure-btn">
              Close
            </button>
          </div>
        </div>
      )}

      {showConnectSuccess && (
        <div className="connect-success-notification">
          <div className="connect-success-content">
            <span className="connect-success-icon">✓</span>
            <span className="connect-success-text">Thank you! We'll be in touch soon.</span>
          </div>
        </div>
      )}

      {showHomeValueSuccess && (
        <div className="connect-success-notification">
          <div className="connect-success-content">
            <span className="connect-success-icon">✓</span>
            <span className="connect-success-text">Got it! David will personally reach out to provide your free home evaluation.</span>
          </div>
        </div>
      )}

      {showHomeValueModal && (
        <div className="modal-overlay" onClick={() => setShowHomeValueModal(false)}>
          <div className="connect-modal" role="dialog" aria-modal="true" aria-label="Contact David Hamady" onClick={(e) => e.stopPropagation()}>
            <h3>Free Home Value Estimate</h3>
            <form onSubmit={handleHomeValueSubmit}>
              <HoneypotField />
              <input type="text" placeholder="Full Name" value={homeValueForm.name} onChange={(e) => setHomeValueForm({...homeValueForm, name: e.target.value})} required />
              <input type="email" placeholder="Email" value={homeValueForm.email} onChange={(e) => setHomeValueForm({...homeValueForm, email: e.target.value})} required />
              <input type="tel" placeholder="Phone Number" value={homeValueForm.phoneNumber} onChange={(e) => setHomeValueForm({...homeValueForm, phoneNumber: e.target.value})} required />
              <input type="text" placeholder="ZIP Code" value={homeValueForm.postalCode} onChange={(e) => setHomeValueForm({...homeValueForm, postalCode: e.target.value})} required />
              <label className="sms-consent-control">
                <input type="checkbox" checked={homeValueForm.smsConsent} onChange={(e) => setHomeValueForm({...homeValueForm, smsConsent: e.target.checked})} />
                <span>I agree to receive text messages about this request. Consent is not a condition of service. Message and data rates may apply; reply STOP to opt out.</span>
              </label>
              <button type="submit" disabled={submittingHomeValue}>{submittingHomeValue ? 'Submitting...' : 'Submit'}</button>
              <button type="button" onClick={() => setShowHomeValueModal(false)} className="cancel-btn">Cancel</button>
            </form>
          </div>
        </div>
      )}

      {showConnectModal && (
        <div className="modal-overlay" onClick={() => setShowConnectModal(false)}>
          <div className="connect-modal" role="dialog" aria-modal="true" aria-label="Contact David Hamady" onClick={(e) => e.stopPropagation()}>
            <h3>Ready to Connect?</h3>
            <form onSubmit={handleConnectSubmit}>
              <HoneypotField />
              <input
                type="text"
                placeholder="Full Name"
                value={connectForm.name}
                onChange={(e) => setConnectForm({...connectForm, name: e.target.value})}
                required
              />
              <input
                type="email"
                placeholder="Email"
                value={connectForm.email}
                onChange={(e) => setConnectForm({...connectForm, email: e.target.value})}
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                value={connectForm.phoneNumber}
                onChange={(e) => setConnectForm({...connectForm, phoneNumber: e.target.value})}
                required
              />
              <input
                type="text"
                placeholder="ZIP Code"
                value={connectForm.postalCode}
                onChange={(e) => setConnectForm({...connectForm, postalCode: e.target.value})}
                required
              />
              <label className="sms-consent-control">
                <input type="checkbox" checked={connectForm.smsConsent} onChange={(e) => setConnectForm({...connectForm, smsConsent: e.target.checked})} />
                <span>I agree to receive text messages about this request. Consent is not a condition of service. Message and data rates may apply; reply STOP to opt out.</span>
              </label>
              <select
                value={connectForm.serviceReq}
                onChange={(e) => setConnectForm({...connectForm, serviceReq: e.target.value})}
                required
              >
                <option value="buying">Buying</option>
                <option value="selling">Selling</option>
                <option value="both">Both</option>
              </select>
              <button type="submit" disabled={submittingConnect}>
                {submittingConnect ? 'Submitting...' : 'Submit'}
              </button>
              <button type="button" onClick={() => setShowConnectModal(false)} className="cancel-btn">
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="assistant-experience">
      <PropertyDetailModal
        propertyDetail={propertyDetail}
        propertyDetailImages={propertyDetailImages}
        imagesLoading={imagesLoading}
        onClose={() => {
          closePropertyDetail();
          window.history.pushState(null, '', '/');
        }}
        user={user}
        isAdmin={isAdmin}
        likedProperties={likedProperties}
        setLikedProperties={setLikedProperties}
      />
      </div>

    </div>
  );
};

export default LandingPage;
